import {
  Box,
  CollapsableCard,
  Spinner,
  Text,
  TogglerCheckbox,
} from '@factorialco/deprecated-design-system'

import FormRow from 'modules/core/components/FormRow'
import Selector from 'modules/core/components/Selector'
import i18n from 'modules/core/lib/i18n'

import { processesOptions } from 'modules/processes/components/WorkflowSelector'
import { useAssignableProcesses } from 'modules/processes/hooks/useAssignableProcesses'

const NOW = 'now'

type SelectableSectionProps = {
  title: string
  type: string
  description: string
  selected?: boolean
  onChange: () => void
}

const Card = ({
  open,
  onChange,
  title,
  description,
}: {
  open: boolean
  title: string
  description: string
  onChange: () => void
}) => {
  return (
    <Box textAlign="left" gap="s8">
      <Box flexDirection="row" justifyContent="spaceBetween" gap="s16" alignItems="center">
        <Box style={{ maxWidth: '90%' }} flexDirection="row" alignItems="center" gap="s8">
          <Text.Normal weight="medium" lineClamp={3} color="grey1000">
            {title}
          </Text.Normal>
        </Box>
        <TogglerCheckbox value={open} onChange={onChange} />
      </Box>
      <Box flexDirection="row" alignItems="center">
        {description && (
          <Text.Normal color="grey800" size="200" lineClamp={3}>
            {description}
          </Text.Normal>
        )}
      </Box>
    </Box>
  )
}

const SelectableSection = ({
  type: _type,
  title,
  description,
  onChange,
  selected,
  children,
}: React.PropsWithChildren<SelectableSectionProps>) => {
  return (
    <CollapsableCard
      open={!!selected}
      Card={<Card open={!!selected} onChange={onChange} title={title} description={description} />}
      Content={children}
    />
  )
}

interface Props {
  enabled: boolean
  onChange: (value: boolean) => void
  selectedWorkflow?: string | number
  onWorkflowSelect: (template: string | undefined) => void
  isDisabled?: boolean
  error?: string
  isOpen?: boolean
  onToggle?: (isOpen: boolean) => void
  mandatory?: boolean
}

function EmployeeOnboardingCard({
  enabled,
  onChange,
  selectedWorkflow,
  onWorkflowSelect,
  isDisabled,
  error,
  isOpen,
  onToggle,
  mandatory,
}: Props) {
  const { loading, processes } = useAssignableProcesses(['onboarding'])

  const handleToggleChange = () => {
    if (enabled) {
      onWorkflowSelect(undefined)
    }
    onChange(!enabled)
  }

  if (loading)
    return (
      <FormRow>
        <Box flexGrow justifyContent="center" alignItems="center">
          <Spinner />
        </Box>
      </FormRow>
    )

  return (
    <FormRow>
      <Box gap="s6">
        <Text.Normal weight="medium" color="grey1000">
          {i18n.t('employee_form.wizard.onboarding_step.onboarding_workflow_selector.label')}
        </Text.Normal>
        <Box gap="s16">
          <SelectableSection
            type={NOW}
            selected={enabled}
            onChange={handleToggleChange}
            description={i18n.t(
              'employee_form.wizard.onboarding_step.onboarding_workflow_selector.description'
            )}
            title={i18n.t(
              'employee_form.wizard.onboarding_step.onboarding_workflow_selector.title'
            )}
          >
            <Selector.Popover
              label={i18n.t(
                'employee_form.wizard.onboarding_step.onboarding_workflow_selector.selector_label'
              )}
              blankLabel=""
              isOpen={isOpen}
              onToggle={onToggle}
              searchPlaceholder=""
              placeholder={i18n.t(
                'employee_form.wizard.onboarding_step.onboarding_workflow_selector.placeholder'
              )}
              onChange={(value) => onWorkflowSelect(value ? `${value}` : undefined)}
              value={selectedWorkflow ? [+selectedWorkflow] : []}
              isDisabled={isDisabled}
              mandatory={mandatory}
              error={error}
            >
              {processesOptions(processes)}
            </Selector.Popover>
          </SelectableSection>
        </Box>
      </Box>
    </FormRow>
  )
}

export default EmployeeOnboardingCard
